import { useTranslations } from "next-intl";
import { BounceLoader } from "@/components/common/bounce-loader";
import { OmnibusLowestPrice } from "@/features/diets/components/omnibus-lowest-price";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";

type SummaryDayPriceRowProps = {
  days: number;
  dailyPrice: number;
  total: number;
  // Colours the per-day price as promotional. The regular price is carried by
  // a separate struck-through row, so the discount is signalled by colour only.
  discounted?: boolean;
  // Omnibus (MPL-1835): L per day, shown under the row when the reduction may
  // be signalled.
  lowestPrice?: number | null;
  loading?: boolean;
  // Padding is left to the caller: the configuration aside renders this row as
  // a standalone card row, the checkout sidebar nests it inside a padded
  // product block.
  className?: string;
};

// "N days × price per day" on the left, position total on the right (DS
// itemDayPrice). Shared by the diet configuration aside and the checkout
// sidebar so both price the position the same way.
export const SummaryDayPriceRow = ({
  days,
  dailyPrice,
  total,
  discounted = false,
  lowestPrice,
  loading = false,
  className,
}: SummaryDayPriceRowProps) => {
  const t = useTranslations();

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {/* One line of inline text on a shared baseline: centring flex boxes
          drifts the bold prices off the regular text (Aeonik metrics, Firefox). */}
      <div className="flex items-baseline justify-between gap-2 text-base">
        <span className="text-brand-green1-300">
          {t("common.days", { count: days })} ×{" "}
          {loading ? (
            <BounceLoader className="inline-flex h-4 align-middle" />
          ) : (
            <span
              className={cn(
                "font-bold",
                discounted ? "text-brand-red" : "text-brand-green1-300",
              )}
            >
              {getLocalePrice({ price: dailyPrice })}
            </span>
          )}
        </span>
        <span className="font-bold text-brand-green1-300">
          {loading ? (
            <BounceLoader className="inline-flex h-4 align-middle" />
          ) : (
            getLocalePrice({ price: total })
          )}
        </span>
      </div>
      {lowestPrice != null && !loading && (
        <OmnibusLowestPrice lowestPrice={lowestPrice} />
      )}
    </div>
  );
};
